import { Bookmark, BookmarkPlus, Rewind, Trash2, Loader2 } from 'lucide-react';
import type { SessionTag } from '../api/types';
import './TagPanel.css';

interface Props {
  tags: SessionTag[];
  loading: boolean;
  /** Tag name currently being resumed/cleared (disables its row), or null. */
  resuming: string | null;
  /** The tag the agent is currently pinned/rewound to (highlighted). */
  pinnedTag?: string | null;
  onBookmark: () => void;
  onRemove: (tag: string) => void;
  onResume: (tag: string) => void;
}

function when(created: number): string {
  const d = (Date.now() - created * 1000) / 1000;
  if (d < 60) return 'gerade';
  if (d < 3600) return `vor ${Math.floor(d / 60)}m`;
  if (d < 86400) return `vor ${Math.floor(d / 3600)}h`;
  if (d < 604800) return `vor ${Math.floor(d / 86400)}d`;
  return new Date(created * 1000).toLocaleDateString('de-DE');
}

/**
 * History-tags panel (Cloud 2026-06-23, #1204 follow-up). A bookmark + rewind
 * surface: "Lesezeichen hier setzen" tags the latest settled turn, the list
 * lets you rewind the agent to any tag (non-destructive branch + restart) or
 * delete it. Mirrors the .ahx-* group-chat dropdown for layout consistency.
 */
export function TagPanel({ tags, loading, resuming, pinnedTag, onBookmark, onRemove, onResume }: Props) {
  const sorted = [...tags].sort((a, b) => b.created_at - a.created_at);
  return (
    <div className="tagp">
      <div className="tagp-top">
        <span className="tagp-label"><Bookmark size={13} /> Lesezeichen</span>
        <button className="tagp-add" onClick={onBookmark}>
          <BookmarkPlus size={14} /> Hier setzen
        </button>
      </div>

      {loading && tags.length === 0 && (
        <div className="tagp-empty"><Loader2 size={14} className="tagp-spin" /> Lade…</div>
      )}
      {!loading && tags.length === 0 && (
        <div className="tagp-empty-big">
          Noch keine Lesezeichen. Mit „Hier setzen" den aktuellen Stand markieren —
          du kannst den Agenten später dorthin zurückspulen.
        </div>
      )}

      {sorted.map((t) => {
        const isPinned = pinnedTag === t.tag;
        const busy = resuming === t.tag;
        return (
          <div key={t.tag} className={`tagp-row${isPinned ? ' pinned' : ''}`}>
            <div className="tagp-row-main">
              <div className="tagp-row-name">
                <Bookmark size={12} /> {t.tag}
                {isPinned && <span className="tagp-pinbadge">aktiv</span>}
              </div>
              <div className="tagp-row-when">{when(t.created_at)}</div>
            </div>
            <div className="tagp-row-actions">
              <button
                className="tagp-resume"
                onClick={() => onResume(t.tag)}
                disabled={busy || isPinned}
                title={isPinned ? 'Agent ist bereits hier' : 'Agent auf diesen Punkt zurückspulen'}
              >
                {busy ? <Loader2 size={13} className="tagp-spin" /> : <Rewind size={13} />}
              </button>
              <button
                className="tagp-del"
                onClick={() => onRemove(t.tag)}
                disabled={busy}
                title="Lesezeichen löschen"
              >
                <Trash2 size={13} />
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
